import { useState, useEffect, useRef } from 'react';
import { submitAttempt } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './TriviaForge.css';

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

export default function TriviaForge({ puzzle, onSolve, alreadyAttempted, onPlayAgain, loadingPlayAgain }) {
  const { user, fetchCredits } = useAuth();

  const [current, setCurrent]       = useState(0);
  const [selected, setSelected]     = useState(null);
  const [answers, setAnswers]       = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [finished, setFinished]     = useState(false);
  const [elapsed, setElapsed]       = useState(0);

  const startTimeRef = useRef(null);
  const timerRef     = useRef(null);

  // On puzzle change: reset game state and timer
  useEffect(() => {
    setCurrent(0);
    setSelected(null);
    setAnswers([]);
    setShowResult(false);
    setFinished(false);
    setElapsed(0);

    if (!puzzle?.id) return;

    // Persist start time across tab switches
    const key = `tf_start_${puzzle.id}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, Date.now().toString());
    }
    startTimeRef.current = parseInt(sessionStorage.getItem(key));

    // Start timer
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [puzzle?.id]);

  // Stop timer when finished
  useEffect(() => {
    if (finished) clearInterval(timerRef.current);
  }, [finished]);

  if (!puzzle?.content) return <div className="tf-loading mono">Loading trivia...</div>;

  if (loadingPlayAgain) return (
    <div className="tf-loading mono" style={{textAlign:'center', padding:'40px 20px'}}>
      <div style={{fontSize:'32px', marginBottom:'12px'}}>⏳</div>
      <div>✨ Generating new puzzle powered by AI</div>
    </div>
  );

  if (alreadyAttempted) return (
    <div className="tf-loading" style={{textAlign:'center', padding:'40px 20px'}}>
      <div style={{fontSize:'32px', marginBottom:'12px', filter:'none', opacity:1}}><span style={{color:'initial'}}>✅</span></div>
      <div style={{fontSize:'16px', fontWeight:'700', color:'#f0f4ff', marginBottom:'8px'}}>Already completed today!</div>
      <div style={{fontSize:'12px', color:'rgba(240,244,255,0.5)', marginBottom:'16px'}}>Use 1 credit to play a new puzzle.</div>
      {onPlayAgain
        ? <button onClick={onPlayAgain} style={{fontSize:'12px', color:'#4fe3c1', background:'rgba(79,227,193,0.1)', border:'1px solid rgba(79,227,193,0.35)', borderRadius:'8px', padding:'8px 16px', cursor:'pointer'}}>✨ Play again — 1 credit</button>
        : <a href="/upgrade" style={{fontSize:'12px', color:'#e8c547', textDecoration:'none'}}>Get more credits →</a>
      }
    </div>
  );

  const { topic, topic_emoji, questions } = puzzle.content;
  const q = questions[current];
  const score = answers.filter(a => a.correct).length;

  const handleSelect = (idx) => {
    if (selected !== null) return;
    setSelected(idx);
    setShowResult(true);
  };

  const handleNext = () => {
    const isCorrect = selected === q.answer_index;
    const newAnswers = [...answers, { correct: isCorrect, selected }];
    setAnswers(newAnswers);
    setSelected(null);
    setShowResult(false);

    if (current + 1 >= questions.length) {
      setFinished(true);
      const solved = newAnswers.filter(a => a.correct).length === questions.length;
      const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000);
      if (user && puzzle) {
        submitAttempt({
          puzzle_id: puzzle.id,
          solved,
          num_guesses: questions.length,
          guesses: [],
          score: Math.round((newAnswers.filter(a => a.correct).length / questions.length) * 100),
          time_taken_seconds: timeTaken,
        }).then(() => { if (fetchCredits) fetchCredits(); }).catch(console.error);
      }
      if (onSolve) onSolve(solved);
    } else {
      setCurrent(current + 1);
    }
  };

  if (finished) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="tf-result">
        <div className="tf-result-icon">{topic_emoji}</div>
        <h2 className="mono">{topic}</h2>
        <div className="tf-timer-result mono">⏱ {formatTime(elapsed)}</div>
        <div className="tf-score-big mono">{score}/{questions.length}</div>
        <div className="tf-score-pct mono">{pct}% correct</div>
        <div className={`tf-verdict mono ${pct === 100 ? 'perfect' : pct >= 60 ? 'good' : 'try-again'}`}>
          {pct === 100 ? '🏆 Perfect score!' : pct >= 80 ? '🎉 Excellent!' : pct >= 60 ? '👍 Good job!' : '📚 Keep learning!'}
        </div>
        <div className="tf-answers-review">
          {questions.map((q, i) => (
            <div key={i} className={`tf-review-item ${answers[i]?.correct ? 'correct' : 'wrong'}`}>
              <span className="mono">{answers[i]?.correct ? '✓' : '✗'}</span>
              <span>{q.question}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tf-wrap">
      <div className="tf-header">
        <div className="tf-topic">
          <span className="tf-emoji">{topic_emoji}</span>
          <div>
            <span className="tf-topic-label mono">Today's Topic</span>
            <span className="tf-topic-name mono">{topic}</span>
          </div>
        </div>
        <div className="tf-header-right">
          <span className="tf-timer mono">⏱ {formatTime(elapsed)}</span>
          <div className="tf-progress-dots">
            {questions.map((_, i) => (
              <div
                key={i}
                className={`tf-dot ${i < answers.length ? (answers[i].correct ? 'correct' : 'wrong') : i === current ? 'active' : ''}`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="tf-question-card">
        <div className="tf-q-num mono">Question {current + 1} of {questions.length}</div>
        <div className="tf-q-text">{q.question}</div>
      </div>

      <div className="tf-options">
        {q.options.map((opt, idx) => {
          let cls = 'tf-option';
          if (selected !== null) {
            if (idx === q.answer_index) cls += ' correct';
            else if (idx === selected) cls += ' wrong';
            else cls += ' dimmed';
          }
          return (
            <button
              key={idx}
              className={`${cls} mono`}
              onClick={() => handleSelect(idx)}
              disabled={selected !== null}
            >
              <span className="tf-opt-letter">{['A','B','C','D'][idx]}</span>
              <span>{opt}</span>
            </button>
          );
        })}
      </div>

      {showResult && (
        <div className="tf-explanation">
          <div className={`tf-verdict-inline mono ${selected === q.answer_index ? 'correct' : 'wrong'}`}>
            {selected === q.answer_index ? '✓ Correct!' : `✗ Wrong — ${q.options[q.answer_index]} was correct`}
          </div>
          <p className="tf-explain-text">{q.explanation}</p>
          <button className="tf-next mono" onClick={handleNext}>
            {current + 1 >= questions.length ? 'See Results →' : 'Next Question →'}
          </button>
        </div>
      )}
    </div>
  );
}
